import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { useTenant } from '../contexts/TenantContext';
import { TenantSelectionView } from './TenantSelectionView';
import { TenantPermission } from '../types/permissions';

const getRoleColor = (role: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (role) {
    case 'admin':
      return 'error';
    case 'operator':
      return 'warning';
    case 'viewer':
      return 'info';
    default:
      return 'default';
  }
};

export const TenantSelector: React.FC = () => {
  const { selectedTenant, availableTenants, setSelectedTenant, loading } = useTenant();
  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Loading tenants...
        </Typography>
      </Box>
    );
  }

  if (availableTenants.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HomeIcon color="action" fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          No tenant access
        </Typography>
      </Box>
    );
  }

  const handleOpenSwitch = () => setSwitchDialogOpen(true);
  const handleCloseSwitch = () => setSwitchDialogOpen(false);
  const handleSelectTenant = (tenant: TenantPermission) => {
    setSelectedTenant(tenant);
    handleCloseSwitch();
  };

  return (
    <>
      <Box
        component="button"
        onClick={availableTenants.length > 1 ? handleOpenSwitch : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          border: 'none',
          background: 'transparent',
          color: 'text.primary',
          cursor: availableTenants.length > 1 ? 'pointer' : 'default',
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          '&:hover': availableTenants.length > 1 ? {
            backgroundColor: theme.palette.action.hover,
          } : {},
        }}
      >
        <HomeIcon color="primary" fontSize="small" />
        {selectedTenant ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
              {selectedTenant.tenant_name}
            </Typography>
            <Chip
              label={selectedTenant.role}
              size="small"
              color={getRoleColor(selectedTenant.role)}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {availableTenants.length > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Click to switch
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select tenant
          </Typography>
        )}
      </Box>

      <Dialog
        open={switchDialogOpen}
        onClose={handleCloseSwitch}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            background: theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(12px)',
          },
        }}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          <Typography variant="h5" fontWeight={600}>
            Switch tenant
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Choose which tenant to work with
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TenantSelectionView
            tenants={availableTenants}
            onSelect={handleSelectTenant}
            title=""
            subtitle=""
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
